import React, { useState, useEffect } from 'react';
import ReactMde from 'react-mde';
import Showdown from 'showdown';
import 'react-mde/lib/styles/css/react-mde-all.css';

function NewPostPage({
  darkMode,
  isEditing,
  newPost,
  setNewPost,
  handlePostSubmit,
  setActivePage,
  setIsEditing,
}) {
  // State to control which tab is selected ("write" or "preview")
  const [selectedTab, setSelectedTab] = useState('write');

  // Set up a converter for GitHub-flavored Markdown
  const converter = new Showdown.Converter({
    tables: true,
    simplifiedAutoLink: true,
    strikethrough: true,
    tasklists: true,
  });

  // Apply dark mode styles directly to the textarea element
  useEffect(() => {
    if (darkMode) {
      // Target the actual textarea element inside ReactMde
      const textarea = document.querySelector('.mde-text');
      if (textarea) {
        textarea.style.backgroundColor = '#2d3748';
        textarea.style.color = '#fff';
      }
    }
  }, [darkMode, selectedTab]); // Re-apply when tab changes or dark mode toggles

  return (
    <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
      <h2 className="text-3xl font-bold mb-6">
        {isEditing ? 'Edit Blog Post' : 'Create New Blog Post'}
      </h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handlePostSubmit(e);
        }}
      >
        <div className="mb-4">
          <label className="block mb-2 font-medium">Title</label>
          <input
            type="text"
            value={newPost.title}
            onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
            className={`w-full p-2 rounded-md ${
              darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white border-gray-300'
            } border`}
            placeholder="Enter post title"
            required
          />
        </div>
        <div className="mb-4">
          <label className="block mb-2 font-medium">Category</label>
          <select
            value={newPost.category}
            onChange={(e) => setNewPost({ ...newPost, category: e.target.value })}
            className={`w-full p-2 rounded-md ${
              darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white border-gray-300'
            } border`}
          >
            <option value="Personal">Personal</option>
            <option value="Technology">Technology</option>
            <option value="Design">Design</option>
            <option value="Business">Business</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="mb-4">
          <label className="block mb-2 font-medium">Content</label>
          <div className={darkMode ? "dark-mode-editor" : ""}>
            <ReactMde
              value={newPost.content}
              onChange={(value) => setNewPost({ ...newPost, content: value })}
              selectedTab={selectedTab}
              onTabChange={setSelectedTab}
              generateMarkdownPreview={(markdown) =>
                Promise.resolve(converter.makeHtml(markdown))
              }
            />
          </div>
          
          {/* Using CSS instead of inline styles for better specificity */}
          <style jsx global>{`
            /* Override ReactMde's default styles with higher specificity */
            .dark-mode-editor .mde-text {
              background-color: #2d3748 !important;
              color: #ffffff !important;
            }
            
            .dark-mode-editor .mde-preview {
              background-color: #2d3748 !important;
              color: #ffffff !important;
            }
            
            .dark-mode-editor .mde-header {
              background-color: #4a5568 !important;
            }
            
            .dark-mode-editor .mde-tabs button {
              color: #e2e8f0 !important;
            }
            
            .dark-mode-editor .mde-tabs button.selected {
              background-color: #2d3748 !important;
            }
          `}</style>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => {
              setNewPost({ title: '', content: '', category: 'Personal' });
              setIsEditing(false);
              setActivePage('blog');
            }}
            className={`px-4 py-2 rounded-md ${
              darkMode ? 'bg-gray-600 hover:bg-gray-700' : 'bg-gray-300 hover:bg-gray-400'
            }`}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`px-4 py-2 rounded-md ${
              darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
            } text-white`}
          >
            {isEditing ? 'Update Post' : 'Publish Post'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default NewPostPage;